import React from "react";
import { Button } from "@/components/ui/button";
import { TrendingUp, PiggyBank, BadgeDollarSign, ArrowRight, PhoneCall, Sparkles, Check, CalendarClock } from "lucide-react";

export function PricingCompare({ onSchedule }) {
  return (
    <section data-testid="pricing-compare" className="mx-auto max-w-7xl px-6 py-20">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">// The math is simple</p>
      <h2 className="mt-2 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
        A fraction of the price. An asset worth far more.
      </h2>

      <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="border border-white/10 bg-[#0A0A0B] p-7">
          <div className="flex h-10 w-10 items-center justify-center border border-white/10 bg-white/[0.03] text-zinc-400">
            <BadgeDollarSign className="h-5 w-5" />
          </div>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Typical agency</p>
          <p className="mt-1 font-display text-4xl font-black text-zinc-400 line-through decoration-red-500/60">$15k–$20k</p>
          <p className="mt-3 text-sm text-zinc-500">What most shops charge for a custom build.</p>
        </div>

        <div className="relative border border-primary/50 bg-primary/[0.06] p-7 shadow-[0_0_40px_rgba(59,130,246,0.15)]">
          <div className="flex h-10 w-10 items-center justify-center border border-primary/40 bg-primary/15 text-primary">
            <PiggyBank className="h-5 w-5" />
          </div>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-primary">With Sites UX</p>
          <p className="mt-1 font-display text-3xl font-black text-white sm:text-4xl">Pay what you're willing to pay</p>
          <p className="mt-3 text-sm text-zinc-400">Your complete custom build — inspiration-matched and 3D-capable.</p>
        </div>

        <div className="border border-white/10 bg-[#0A0A0B] p-7">
          <div className="flex h-10 w-10 items-center justify-center border border-white/10 bg-white/[0.03] text-emerald-400">
            <TrendingUp className="h-5 w-5" />
          </div>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Resale value of your code</p>
          <p className="mt-1 font-display text-4xl font-black text-emerald-400">$60k–$80k</p>
          <p className="mt-3 text-sm text-zinc-500">You own 100% of the source — a real, sellable asset.</p>
        </div>
      </div>

      <p className="mt-6 max-w-2xl text-sm text-zinc-400">
        <span className="text-white">Pay what you're willing to pay</span> — and walk away owning code that can resell for
        <span className="text-emerald-400"> $60,000–$80,000</span>. That's the difference between renting a website builder and owning your digital asset.
      </p>

      {/* One flat price — inspiration + 3D capabilities */}
      <div data-testid="option-inspiration" className="beam-border group relative mt-12 overflow-hidden border border-primary/40 bg-[#08080B] p-8 md:p-12">
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full opacity-40 blur-3xl transition-opacity duration-500 group-hover:opacity-70"
          style={{ background: "radial-gradient(circle, rgba(59,130,246,0.55), transparent 70%)" }}
        />
        <div className="relative grid grid-cols-1 items-center gap-8 md:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-3 py-1 font-mono text-xs uppercase tracking-[0.2em] text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Pay what you're willing to pay
            </span>
            <h3 className="mt-6 font-display text-3xl font-black uppercase leading-none tracking-tighter sm:text-4xl">
              One price.<br />Everything you can dream up.
            </h3>
            <p className="mt-5 max-w-md text-zinc-300">
              Found a website you love? Send it over — we'll take it as inspiration and build you something
              even better. Want it <span className="text-white">fully 3D-animated</span> with immersive, interactive
              motion? We do that too.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                "Bring any site you like as inspiration",
                "3D-animated & interactive, if you want",
                "Fully custom, hand-coded design",
                "You own 100% of the source",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col items-start gap-4 border-t border-white/10 pt-8 md:items-end md:border-l md:border-t-0 md:pl-10 md:pt-0 md:text-right">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Your call · you own it</p>
            <p data-testid="option-price" className="font-display text-4xl font-black leading-tight text-white sm:text-5xl" style={{ textShadow: "0 0 40px rgba(59,130,246,0.4)" }}>
              Pay what you're<br />willing to pay
            </p>
            <p className="max-w-xs text-sm text-zinc-400 md:text-right">
              Your complete custom website — inspiration-matched, 3D-capable, and entirely yours.
            </p>
            <Button
              data-testid="option-schedule-btn"
              onClick={onSchedule}
              className="mt-2 rounded-full bg-primary px-7 py-6 text-base text-white hover:bg-primary/90 hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-[background-color,box-shadow]"
            >
              <CalendarClock className="mr-2 h-5 w-5" /> Book this build
            </Button>
          </div>
        </div>
      </div>

      {/* References CTA */}
      <div className="mt-10 flex flex-col items-start justify-between gap-6 border border-white/10 bg-[#0A0A0B] p-8 md:flex-row md:items-center">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 text-primary">
            <PhoneCall className="h-5 w-5" />
            <p className="font-mono text-xs uppercase tracking-[0.2em]">Don't just take our word for it</p>
          </div>
          <h3 className="mt-3 font-display text-2xl font-bold">Want proof? Just ask.</h3>
          <p className="mt-2 text-zinc-400">
            Ask us for projects we've already built — or to speak directly with one of our real customers.
            We'll happily connect you so you can hear how we really are.
          </p>
        </div>
        <Button data-testid="references-schedule-btn" onClick={onSchedule}
          className="shrink-0 rounded-full bg-primary px-7 py-6 text-base text-white hover:bg-primary/90 hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-[background-color,box-shadow]">
          Ask us for references <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </section>
  );
}
